import React, { useState, useEffect } from "react";
import { ref, set } from "firebase/database";
import { database } from "../../../firebaseConfig";
import toast from "react-hot-toast";
import { X, Edit } from "lucide-react";

const EditShopModal = ({ shop, companyName, onClose }) => {
  const [formData, setFormData] = useState({
    shopName: "",
    location: "",
    managerName: "",
    phoneNumber: "",
    email: "",
    currentStock: "",
    status: "Active",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (shop) {
      setFormData({
        shopName: shop.shopName || "",
        location: shop.location || "",
        managerName: shop.managerName || "",
        phoneNumber: shop.phoneNumber || "",
        email: shop.email || "",
        currentStock: shop.currentStock || "0",
        status: shop.status || "Active",
      });
    }
  }, [shop]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.shopName.trim()) {
      toast.error("Shop name is required");
      return;
    }

    setLoading(true);
    try {
      const updatedShop = {
        ...shop,
        shopName: formData.shopName,
        location: formData.location,
        managerName: formData.managerName,
        phoneNumber: formData.phoneNumber,
        email: formData.email,
        currentStock: parseFloat(formData.currentStock) || 0,
        status: formData.status,
        updatedAt: new Date().toISOString(),
      };

      await set(ref(database, `Companies/${companyName}/Shops/${shop.id}`), updatedShop);

      toast.success("Shop updated successfully");
      onClose();
    } catch (error) {
      console.error("Error updating shop:", error);
      toast.error("Failed to update shop. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!shop) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-cyan-700 flex items-center gap-2">
            <Edit size={24} />
            Edit Shop Details
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Shop Name *
            </label>
            <input
              type="text"
              name="shopName"
              value={formData.shopName}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location *
            </label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Manager Name
            </label>
            <input
              type="text"
              name="managerName"
              value={formData.managerName}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="text"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Stock (Liters)
            </label>
            <input
              type="number"
              name="currentStock"
              step="0.01"
              min="0"
              value={formData.currentStock}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition disabled:bg-cyan-300"
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditShopModal;
